import { provide, reactive } from 'vue'

import useApi from '@/views/apps/_customPage/api'
import type { ApiCustomPageComponents, ExtendedBlock, RepeatableContainerAddSectionParams, RepeatableContainerRemoveSectionParams } from 'widget-components/integrations/CustomPage/types'
import { lazyBlockToolsInjectionKey } from 'widget-components/integrations/CustomPage/injection-keys'
import type { AxiosError } from 'axios'
import { until } from '@vueuse/core'
import useWidgetViewManager from '@/composables/widget-view-manager'
import { captureException } from '@sentry/vue'

const getErrorMessage = (error: unknown) => {
    const axiosError = error as AxiosError<{
        errors: { code: string }[]
    }>
    const apiError = axiosError.response?.data as { errors: Record<'code' | 'message', string>[] }

    return Array.isArray(apiError?.errors)
        ? apiError
            ?.errors
            .map(e => e.message || e.code)
            .filter(Boolean)
            .join(', ')
        : axiosError.message
}

export function useLazyBlockTools() {
    const { activeMessage } = useWidgetViewManager()
    const isCustomPage2 = activeMessage.value?.taskType === 'CUSTOM_PAGE_2'

    const { getLazyBlock, getInputCountryBlock, getInputPhoneNumberBlock, postRepeatableContainerSection, deleteRepeatableContainerSection } = useApi()

    const lazyBlocks = reactive<Record<string, ExtendedBlock & { loading: boolean }>>({})

    const setLazyBlockProp = (id: keyof typeof lazyBlocks, key: string | keyof ExtendedBlock, value: unknown) => {
        Object.assign(lazyBlocks, {
            [id]: {
                ...lazyBlocks[id],
                [key]: value,
            },
        })
    }

    const requestBlockConfig = async ({ id, type }: {id: string | keyof typeof lazyBlocks, type: string}) => {
        if (type === 'INPUT_COUNTRY' || type === 'INPUT_PHONE_NUMBER') {
            try {
                if (lazyBlocks[id] && lazyBlocks[id].loading !== undefined) {
                    await until(() => {
                        return lazyBlocks[id].loading
                    }).toBe(false, {
                        timeout: 5000,
                        throwOnTimeout: true,
                    })
                    return
                } else {
                    setLazyBlockProp(id, 'error', '')
                    setLazyBlockProp(id, 'loading', true)
                }

                const res = isCustomPage2
                    ? await getLazyBlock({ componentId: id })
                    : type === 'INPUT_COUNTRY'
                        ? await getInputCountryBlock({ componentId: id })
                        : await getInputPhoneNumberBlock({ componentId: id })

                if (res.data) {
                    Object.assign(lazyBlocks, {
                        [id]: {
                            id,
                            type,
                            items: res.data,
                        },
                    })
                }
            } catch (error) {
                const errorMessage = getErrorMessage(error)

                // eslint-disable-next-line no-console
                console.error(errorMessage)

                setLazyBlockProp(id, 'error', errorMessage)
                captureException(error)
            } finally {
                setLazyBlockProp(id, 'loading', false)
            }
        }
    }

    const getLazyBlockProp = (
        id: string,
        key: string | keyof ExtendedBlock<ApiCustomPageComponents>,
    ) => {
        return lazyBlocks[id]?.[key as keyof ExtendedBlock<ApiCustomPageComponents>]
    }

    type ComponentAction = 'add-section' | 'remove-section'
    type ComponentActionParams = RepeatableContainerAddSectionParams | RepeatableContainerRemoveSectionParams

    const dispatchComponentAction = async (
        action: ComponentAction,
        params: ComponentActionParams,
    ) => {
        try {
            switch (action) {
                case 'add-section':
                    return (await postRepeatableContainerSection(params as RepeatableContainerAddSectionParams)).data
                case 'remove-section':
                    await deleteRepeatableContainerSection(params as RepeatableContainerRemoveSectionParams)
                    return (params as RepeatableContainerRemoveSectionParams).sectionId
                default:
                    throw new Error(`Unknown action type: ${action}`)
            }
        } catch (error) {
            const errorMessage = getErrorMessage(error)
            // eslint-disable-next-line no-console
            console.error(errorMessage)
            setLazyBlockProp(params.blockId, 'error', errorMessage)
            captureException(error)
        }
    }

    provide(lazyBlockToolsInjectionKey, {
        requestBlockConfig,
        getLazyBlockProp,
        dispatchComponentAction,
    })

    return {
        lazyBlocks,
        requestBlockConfig,
        setLazyBlockProp,
        getLazyBlockProp,
        dispatchComponentAction,
    }
}
